import { Id } from '@/infra/id'
import type { TimeRange } from '@/infra/time-range'
import { Model, type ModelProps } from '@/infra/model'
import type { ConsultationDisplayStage } from './consultation-display-stage'
import {
  matchConsultationDisplayStage,
  type MatchConsultationDisplayArms,
} from './consultation-display-stage'
import { computed, observable } from 'mobx'
import type { User } from '@/features/iam/users/User'
import { clock } from '@/utils/rx-util'
import { asAtom } from '@/utils/mobx-util'
import { isAfter } from 'date-fns'
import { addHours } from 'date-fns'

/**
 * Props for {@link Consultation}.
 */
export interface ConsultationProps extends ModelProps<Consultation> {}

/**
 * Represents a consultation.
 */
export class Consultation extends Model<Consultation> {
  /**
   * The current time as an observable that ticks every 5 seconds.
   */
  private readonly _now = asAtom(clock(5_000), () => new Date())

  id: Id = Id.none
  @observable accessor jobId = Id.none
  @observable accessor availability: TimeRange[] = []
  @observable accessor availabilityCreateTime: Date | null = null
  @observable accessor clientUserPublicId = ''
  @observable accessor createdByUserPublicId = ''
  @observable accessor bookedTime: TimeRange | null = null
  @observable accessor bookingCreateTime: Date | null = null
  @observable accessor createTime: Date | null = null
  @observable accessor requestedLength = 30
  @observable accessor skipScheduling = false
  @observable accessor status: ConsultationStatus = 'Unknown'
  @observable accessor roomId = ''
  @observable accessor providerUser: User | null = null

  /**
   * The display stage of the consultation.
   */
  @computed
  get displayStage(): ConsultationDisplayStage {
    if (this.skipScheduling) {
      return {
        type: 'InProgress',
      }
    }

    // Client rescheduled the call
    if (this.status === 'PendingBooking' && this.providerUser) {
      return {
        type: 'AwaitingProConfirmation',
        availability: this.availability.flatMap((range) =>
          range.decomposeRangeInIntervals(this.requestedLength),
        ),
      }
    }

    // Awaiting pickup
    if (
      this.status === 'PendingAvailability' ||
      this.status === 'PendingBooking'
    ) {
      return {
        type: 'AwaitingPickup',
        requestedLength: this.requestedLength,
        availability: this.availability.flatMap((range) =>
          range.decomposeRangeInIntervals(this.requestedLength),
        ),
      }
    }

    // The call is happening today
    if (this.bookedTime?.isSameDay(this._now())) {
      return {
        type: 'DayOfCall',
        bookedTime: this.bookedTime,
        requestedLength: this.requestedLength,
        providerDisplayName: this.providerUser?.displayName || '',
      }
    }

    // Provider has booked the call
    if (this.status === 'PendingCall' && this.bookedTime) {
      return {
        type: 'Booked',
        bookedTime: this.bookedTime,
        requestedLength: this.requestedLength,
        providerDisplayName: this.providerUser?.displayName || '',
      }
    }

    // The call was completed
    if (this.status === 'Done' && this.bookedTime) {
      return {
        type: 'CallCompleted',
        bookedTime: this.bookedTime,
        requestedLength: this.requestedLength,
        providerDisplayName: this.providerUser?.displayName || '',
      }
    }

    return {
      type: 'InProgress',
    }
  }

  /**
   * Whether the availability can be changed.
   *
   * NOTE: Checking this may not be sufficient; for example, certain jobs have
   * additional rules that must be checked.
   */
  @computed
  get isSettingAvailabilityAllowed(): boolean {
    switch (this.status) {
      case 'PendingAvailability':
        return true
      case 'PendingBooking':
      case 'PendingCall':
        // When pending booking or call with a booked slot, only allow
        // setting availability if the booked time is more
        // than 12 hours in the future
        if (!this.bookedTime) {
          return true
        }

        return isAfter(this.bookedTime.startTime, addHours(this._now(), 12))
      default:
        return false
    }
  }

  /**
   * Match on the consultation display stage.
   */
  match<T>(arms: MatchConsultationDisplayArms<T>): T {
    return matchConsultationDisplayStage<T>(this.displayStage, arms)
  }
}

export const allConsultationStatuses = [
  'PendingAvailability',
  'PendingBooking',
  'PendingCall',
  'InProgress',
  'Done',
  'Cancelled',
  'Unknown',
] as const

export type ConsultationStatus = (typeof allConsultationStatuses)[number]
